import React from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer, Pricings } from "./LandingPage/Components";

const Check = () => (
  <span className="pcp-check" aria-label="Included">
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="#45C48A" fillOpacity="0.15" />
      <path
        d="M4.5 8l2.5 2.5 4-4.5"
        stroke="#45C48A"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const Dash = () => <span className="pcp-dash">—</span>;

const COMPARISON_SECTIONS = [
  {
    heading: "Billing",
    rows: [
      {
        label: "14-day paid-plan trial",
        hint: "New signups can trial Standard or Teams after adding a card. Existing Free accounts are charged immediately. SMS unlocks after the trial converts.",
        free: false,
        standard: "Card required",
        teams: "Card required",
      },
    ],
  },
  {
    heading: "Scheduling",
    rows: [
      {
        label: "Connected calendars",
        hint: "Additional staff calendar connections are available by approved request.",
        free: "1 admin calendar",
        standard: "Admin: 6 · Staff: 1 each",
        teams: "Admin: 6 · Staff: 1 each",
      },
      {
        label: "Event types",
        free: "1",
        standard: "Unlimited",
        teams: "Unlimited",
      },
      { label: "Group events", free: false, standard: true, teams: true },
      {
        label: "Personalized MeetOcto link",
        free: false,
        standard: true,
        teams: true,
      },
      {
        label: "Redirect to custom pages",
        free: false,
        standard: true,
        teams: true,
      },
    ],
  },
  {
    heading: "Team assignment",
    rows: [
      {
        label: "Staff per shared event",
        free: false,
        standard: "Based on purchased seats",
        teams: "Based on purchased seats",
      },
      {
        label: "Round Robin assignment",
        free: false,
        standard: "1",
        teams: "Unlimited",
      },
      {
        label: "Even Rotation assignment",
        free: false,
        standard: true,
        teams: true,
      },
    ],
  },
  {
    heading: "Booking controls",
    rows: [
      {
        label: "Booking approval and rejection",
        hint: "There is no limit on booking requests, approvals or rejections within an enabled event type.",
        free: false,
        standard: "1 approval-required event type",
        teams: "Unlimited approval-required event types",
      },
      {
        label: "Automated event notifications",
        free: false,
        standard: false,
        teams: true,
      },
    ],
  },
  {
    heading: "CRM & customer management",
    rows: [
      { label: "Customer directory", free: false, standard: true, teams: true },
      {
        label: "Customer profiles and contact details",
        free: false,
        standard: true,
        teams: true,
      },
      {
        label: "Customer booking history",
        hint: "Review each customer's past and upcoming bookings from their profile.",
        free: false,
        standard: true,
        teams: true,
      },
    ],
  },
  {
    heading: "Branding & customization",
    rows: [
      {
        label: "OctoLink customization",
        free: false,
        standard: true,
        teams: true,
      },
      {
        label: "Remove MeetOcto branding",
        free: false,
        standard: true,
        teams: true,
      },
      {
        label: "Promotional banners",
        free: false,
        standard: true,
        teams: true,
      },
    ],
  },
  {
    heading: "Payments & commerce",
    rows: [
      {
        label: "Collect payments with Stripe or PayPal",
        free: false,
        standard: false,
        teams: true,
      },
      { label: "Coupon codes", free: false, standard: false, teams: true },
      {
        label: "Upsell and cross-sell",
        free: false,
        standard: false,
        teams: true,
      },
      { label: "Marketing tools", free: false, standard: false, teams: true },
    ],
  },
  {
    heading: "Analytics & website",
    rows: [
      {
        label: "Metrics and reporting",
        free: false,
        standard: true,
        teams: true,
      },
      {
        label: "Add OctoLink to website",
        free: false,
        standard: true,
        teams: true,
      },
    ],
  },
  {
    heading: "Integrations",
    rows: [
      { label: "Google Meet", free: true, standard: true, teams: true },
      { label: "Zoom", free: true, standard: true, teams: true },
      { label: "Microsoft Teams", free: true, standard: true, teams: true },
      {
        label: "Google, Office 365 and Outlook Calendar",
        free: true,
        standard: true,
        teams: true,
      },
      {
        label: "Zapier",
        free: "Coming soon",
        standard: "Coming soon",
        teams: "Coming soon",
      },
    ],
  },
];

function CellValue({ value }) {
  if (value === true) return <Check />;
  if (value === false) return <Dash />;
  return <span className="pcp-cell-text">{value}</span>;
}

const PricingCompare = () => {
  return (
    <div className="pricing-compare-page">
      <Navbar />

      <main className="pricing-compare-page__main">
        <header className="pcp-hero" id="plans">
          <span className="pcp-hero__label">Simple, transparent pricing</span>
          <h1 className="pcp-hero__title">
            Scheduling that grows
            <br />
            with you and your team.
          </h1>
          <p className="pcp-hero__subtitle">
            Start free. Standard and Teams include a 14-day trial (card
            required) — then unlock team scheduling and customer management, or
            move to Teams when you need unlimited team workflows.
          </p>
          <div className="pcp-hero__anchors">
            <a href="#plans">Plans</a>
            <a href="#compare">Compare features</a>
            <a href="#calendar-policy">Calendar policy</a>
          </div>
        </header>

        <Pricings hideCompareLink />

        <section id="calendar-policy" className="pcp-policy">
          <div className="pcp-policy__banner">
            <div className="pcp-policy__copy">
              <span className="pcp-policy__label">Calendar connection policy</span>
              <h2 className="pcp-policy__title">
                Reliable calendar synchronization for every organization.
              </h2>
              <p className="pcp-policy__subtitle">
                Calendar limits apply to organization accounts on Standard and
                Teams.
              </p>
            </div>
            <div className="pcp-policy__panel">
              <div className="pcp-policy__col">
                <h3>Organization admin</h3>
                <p className="pcp-policy__value">Up to 6 calendars</p>
                <p>
                  Connect calendars used to manage availability and prevent
                  conflicts.
                </p>
              </div>
              <div className="pcp-policy__col">
                <h3>Each staff member</h3>
                <p className="pcp-policy__value">1 calendar</p>
                <p>Each staff member can connect one calendar by default.</p>
              </div>
              <div className="pcp-policy__col">
                <h3>Need another?</h3>
                <p className="pcp-policy__value">Request access</p>
                <p>
                  Contact Support with your use case. Additional connections
                  require approval.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="compare" className="pcp-compare">
          <div className="pcp-compare__inner">
            <span className="pcp-compare__label">Full feature comparison</span>
            <h2 className="pcp-compare__title">See exactly what is included.</h2>
            <p className="pcp-compare__subtitle">
              Every active admin, host or staff member requires a paid seat on
              Standard and Teams.
            </p>

            <div className="pcp-table-wrap">
              <table className="pcp-table">
                <thead>
                  <tr>
                    <th className="pcp-th-feature">Feature</th>
                    <th>Free</th>
                    <th className="pcp-th-featured">Standard</th>
                    <th>Teams</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_SECTIONS.map((section) => (
                    <React.Fragment key={section.heading}>
                      <tr className="pcp-section-row">
                        <td colSpan={4}>{section.heading}</td>
                      </tr>
                      {section.rows.map((row) => (
                        <tr key={row.label} className="pcp-feature-row">
                          <td className="pcp-td-label">
                            <span>{row.label}</span>
                            {row.hint && (
                              <small className="pcp-hint">{row.hint}</small>
                            )}
                          </td>
                          <td>
                            <CellValue value={row.free} />
                          </td>
                          <td className="pcp-td-featured">
                            <CellValue value={row.standard} />
                          </td>
                          <td>
                            <CellValue value={row.teams} />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="pcp-footnote">
              Prices are in USD and exclude applicable taxes. “Unlimited”
              features remain subject to fair-use and platform policies.
            </p>
          </div>
        </section>

        <section className="pcp-cta">
          <div className="pcp-cta__inner">
            <span className="pcp-cta__label">Ready to schedule better?</span>
            <h2 className="pcp-cta__title">
              Start free. Try Standard or Teams for 14 days.
            </h2>
            <Link to="/signup" className="pcp-cta__btn">
              Get started free
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingCompare;
